import { useEffect, useRef, useState } from "react";
import * as THREE from "three";
import { motion } from "framer-motion";
import { RotateCcw, MousePointer2 } from "lucide-react";

const Product3DViewer = () => {
  const mountRef = useRef(null);
  const stateRef = useRef({});
  const [interacted, setInteracted] = useState(false);

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;

    const width = mount.clientWidth;
    const height = mount.clientHeight;

    const scene = new THREE.Scene();
    scene.background = null;

    const camera = new THREE.PerspectiveCamera(38, width / height, 0.1, 100);
    camera.position.set(0, 0, 5.6);

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(width, height);
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    mount.appendChild(renderer.domElement);

    // ---------- Materials ----------
    const matteBlack = new THREE.MeshStandardMaterial({
      color: 0x111111,
      roughness: 0.62,
      metalness: 0.18,
    });
    const knobMat = new THREE.MeshStandardMaterial({
      color: 0x0a0a0a,
      roughness: 0.45,
      metalness: 0.35,
    });

    // ---------- Product group ----------
    const product = new THREE.Group();
    scene.add(product);

    // Sphere with dimples (subtle): use SphereGeometry, then offset vertices in a noise pattern
    const sphereRadius = 1.25;
    const sphereGeo = new THREE.SphereGeometry(sphereRadius, 128, 128);
    // create dimples by pushing some vertices inward
    {
      const pos = sphereGeo.attributes.position;
      const v = new THREE.Vector3();
      const dimples = [];
      for (let i = 0; i < 40; i++) {
        const u = Math.acos(2 * Math.random() - 1);
        const t = Math.random() * Math.PI * 2;
        dimples.push(new THREE.Vector3(
          Math.sin(u) * Math.cos(t),
          Math.sin(u) * Math.sin(t),
          Math.cos(u)
        ));
      }
      for (let i = 0; i < pos.count; i++) {
        v.fromBufferAttribute(pos, i);
        const n = v.clone().normalize();
        let push = 0;
        for (const d of dimples) {
          const dot = n.dot(d);
          if (dot > 0.93) push += (dot - 0.93) * 0.6;
        }
        v.addScaledVector(n, -push);
        pos.setXYZ(i, v.x, v.y, v.z);
      }
      pos.needsUpdate = true;
      sphereGeo.computeVertexNormals();
    }
    const sphere = new THREE.Mesh(sphereGeo, matteBlack);
    product.add(sphere);

    // 5 knobs at varied directions
    const knobDirs = [
      new THREE.Vector3(0.0, 1.0, 0.15),
      new THREE.Vector3(0.85, 0.55, 0.25),
      new THREE.Vector3(-0.75, 0.45, 0.4),
      new THREE.Vector3(0.95, -0.25, 0.25),
      new THREE.Vector3(-0.85, -0.4, 0.3),
    ];

    knobDirs.forEach((dir) => {
      const d = dir.clone().normalize();
      const stemLen = 0.55;
      const capH = 0.18;
      const capR = 0.18;
      const stemR = 0.08;

      // stem
      const stemGeo = new THREE.CylinderGeometry(stemR, stemR, stemLen, 24);
      const stem = new THREE.Mesh(stemGeo, knobMat);
      // cap
      const capGeo = new THREE.CylinderGeometry(capR, capR, capH, 28);
      const cap = new THREE.Mesh(capGeo, knobMat);
      cap.position.y = stemLen / 2 + capH / 2;

      const knobGroup = new THREE.Group();
      knobGroup.add(stem);
      knobGroup.add(cap);

      // orient along dir
      const base = d.clone().multiplyScalar(sphereRadius - 0.05);
      knobGroup.position.copy(base);
      // direction = local +Y must align with d
      const up = new THREE.Vector3(0, 1, 0);
      const q = new THREE.Quaternion().setFromUnitVectors(up, d);
      knobGroup.quaternion.copy(q);
      // shift so stem base touches sphere
      knobGroup.translateY(stemLen / 2);

      product.add(knobGroup);
    });

    // ---------- Lights ----------
    const ambient = new THREE.AmbientLight(0xffffff, 0.35);
    scene.add(ambient);

    const key = new THREE.DirectionalLight(0xffffff, 1.6);
    key.position.set(3, 4, 5);
    scene.add(key);

    const rimBlue = new THREE.DirectionalLight(0x007AFF, 2.2);
    rimBlue.position.set(-4, 1.5, -3);
    scene.add(rimBlue);

    const fill = new THREE.DirectionalLight(0xffffff, 0.4);
    fill.position.set(-3, -2, 2);
    scene.add(fill);

    // ---------- Interaction ----------
    const s = stateRef.current;
    s.rotX = 0.25;
    s.rotY = 0.7;
    s.targetX = 0.25;
    s.targetY = 0.7;
    s.dragging = false;
    s.lastX = 0;
    s.lastY = 0;
    s.autoRotate = true;

    const onPointerDown = (e) => {
      s.dragging = true;
      s.autoRotate = false;
      setInteracted(true);
      const p = e.touches ? e.touches[0] : e;
      s.lastX = p.clientX;
      s.lastY = p.clientY;
    };
    const onPointerMove = (e) => {
      if (!s.dragging) return;
      const p = e.touches ? e.touches[0] : e;
      const dx = p.clientX - s.lastX;
      const dy = p.clientY - s.lastY;
      s.targetY += dx * 0.008;
      s.targetX += dy * 0.006;
      s.targetX = Math.max(-1.1, Math.min(1.1, s.targetX));
      s.lastX = p.clientX;
      s.lastY = p.clientY;
      if (e.cancelable) e.preventDefault();
    };
    const onPointerUp = () => { s.dragging = false; };

    const el = renderer.domElement;
    el.style.touchAction = "none";
    el.style.cursor = "grab";
    el.addEventListener("mousedown", onPointerDown);
    window.addEventListener("mousemove", onPointerMove);
    window.addEventListener("mouseup", onPointerUp);
    el.addEventListener("touchstart", onPointerDown, { passive: false });
    el.addEventListener("touchmove", onPointerMove, { passive: false });
    el.addEventListener("touchend", onPointerUp);

    // resize
    const onResize = () => {
      const w = mount.clientWidth;
      const h = mount.clientHeight;
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h);
    };
    window.addEventListener("resize", onResize);

    // animate
    let raf;
    const tick = () => {
      if (s.autoRotate && !s.dragging) {
        s.targetY += 0.004;
      }
      s.rotX += (s.targetX - s.rotX) * 0.08;
      s.rotY += (s.targetY - s.rotY) * 0.08;
      product.rotation.x = s.rotX;
      product.rotation.y = s.rotY;
      // gentle bob
      product.position.y = Math.sin(performance.now() * 0.0009) * 0.06;
      renderer.render(scene, camera);
      raf = requestAnimationFrame(tick);
    };
    tick();

    s.reset = () => {
      s.targetX = 0.25;
      s.targetY = 0.7;
      s.autoRotate = true;
      setInteracted(false);
    };

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", onResize);
      el.removeEventListener("mousedown", onPointerDown);
      window.removeEventListener("mousemove", onPointerMove);
      window.removeEventListener("mouseup", onPointerUp);
      el.removeEventListener("touchstart", onPointerDown);
      el.removeEventListener("touchmove", onPointerMove);
      el.removeEventListener("touchend", onPointerUp);
      mount.removeChild(renderer.domElement);
      sphereGeo.dispose();
      matteBlack.dispose();
      knobMat.dispose();
      renderer.dispose();
    };
  }, []);

  const reset = () => stateRef.current?.reset?.();

  return (
    <section id="viewer" data-testid="viewer-section" className="relative py-24 md:py-32 border-t border-white/10 bg-[#0a0a0a] overflow-hidden">
      {/* accent glow */}
      <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
        <div className="w-[700px] h-[700px] rounded-full"
          style={{ background: "radial-gradient(circle, rgba(0,122,255,0.14) 0%, transparent 60%)", filter: "blur(40px)" }} />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.6 }}
          className="flex items-end justify-between flex-wrap gap-6 mb-12 md:mb-16"
        >
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-[#007AFF] mb-4 font-body">// 02.5 Inspect</div>
            <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl leading-[0.95]">
              SPIN IT.<br/>FEEL THE FORM.
            </h2>
          </div>
          <p className="max-w-md text-white/55 text-sm leading-relaxed">
            Drag to rotate. Pinch or swipe on mobile. Every angle, every knob — exactly as it lands in your palm.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.7 }}
          className="relative border border-white/10 bg-[#080808]"
          data-testid="product-3d-viewer"
        >
          <div
            ref={mountRef}
            className="w-full h-[420px] sm:h-[520px] md:h-[600px] select-none"
            data-cursor-hover
            aria-label="3D product viewer — drag to rotate"
            role="img"
          />

          {/* HUD */}
          <div className="absolute top-4 left-4 flex items-center gap-2 px-3 py-2 bg-black/60 border border-white/10 backdrop-blur text-[10px] uppercase tracking-[0.25em] text-white/60 font-body">
            <span className="w-1.5 h-1.5 rounded-full bg-[#007AFF] animate-pulse" />
            GFX-X · LIVE 3D
          </div>

          <div className="absolute top-4 right-4 flex items-center gap-2">
            <button
              onClick={reset}
              className="flex items-center gap-2 px-3 py-2 bg-black/60 border border-white/10 backdrop-blur hover:border-[#007AFF] hover:text-[#007AFF] text-[10px] uppercase tracking-[0.25em] text-white/70 font-body transition"
              data-testid="viewer-reset"
            >
              <RotateCcw className="w-3 h-3" />
              Reset
            </button>
          </div>

          {!interacted && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2 px-4 py-2 bg-black/70 border border-white/10 backdrop-blur text-[10px] uppercase tracking-[0.25em] text-white/70 font-body pointer-events-none"
              data-testid="viewer-hint"
            >
              <MousePointer2 className="w-3 h-3 text-[#007AFF]" />
              Drag / Swipe to rotate
            </motion.div>
          )}

          {/* corner markers */}
          <span className="absolute top-0 left-0 w-3 h-3 border-t border-l border-[#007AFF]" />
          <span className="absolute top-0 right-0 w-3 h-3 border-t border-r border-[#007AFF]" />
          <span className="absolute bottom-0 left-0 w-3 h-3 border-b border-l border-[#007AFF]" />
          <span className="absolute bottom-0 right-0 w-3 h-3 border-b border-r border-[#007AFF]" />
        </motion.div>
      </div>
    </section>
  );
};

export default Product3DViewer;
